import { useCallback, useEffect, useRef, useState } from "react";

import Corbeille from "./Corbeille";
import { api, type Classe, type Eleve, type ImpactClasse } from "./api";

const NIVEAUX = [
  { code: "seconde", libelle: "Seconde" },
  { code: "1spe", libelle: "Première spécialité" },
  { code: "tspe", libelle: "Terminale spécialité" },
];

function anneeScolaireCourante(): string {
  const maintenant = new Date();
  // L'année scolaire bascule en août, pas en janvier.
  const debut = maintenant.getMonth() >= 7
    ? maintenant.getFullYear()
    : maintenant.getFullYear() - 1;
  return `${debut}-${debut + 1}`;
}

export default function Classes() {
  const [classes, setClasses] = useState<Classe[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<number | null>(null);

  const recharger = useCallback(() => {
    api.listerClasses().then(setClasses).catch((e: Error) => setErreur(e.message));
  }, []);

  useEffect(recharger, [recharger]);

  if (erreur) return <p className="erreur">{erreur}</p>;
  if (!classes) return <p>Chargement…</p>;

  return (
    <section>
      <h2>Classes</h2>

      {classes.length === 0 && <p>Aucune classe. Créez-en une ci-dessous.</p>}

      {classes.map((classe) => (
        <FicheClasse
          key={classe.id}
          classe={classe}
          ouverte={ouverte === classe.id}
          basculer={() => setOuverte(ouverte === classe.id ? null : classe.id)}
          surChangement={recharger}
        />
      ))}

      <NouvelleClasse surCreation={recharger} />
      <Corbeille surRestauration={recharger} />
    </section>
  );
}

function FicheClasse({
  classe,
  ouverte,
  basculer,
  surChangement,
}: {
  classe: Classe;
  ouverte: boolean;
  basculer: () => void;
  surChangement: () => void;
}) {
  const [eleves, setEleves] = useState<Eleve[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [impact, setImpact] = useState<ImpactClasse | null>(null);
  const fichier = useRef<HTMLInputElement>(null);
  const supprimable = impact !== null;

  useEffect(() => {
    if (ouverte) api.lireEleves(classe.id).then(setEleves).catch(() => setEleves([]));
  }, [ouverte, classe.id]);

  const importer = async (choisi: File) => {
    try {
      const bilan = await api.importerEleves(classe.id, choisi);
      setMessage(
        bilan.inscrits === 0
          ? `${bilan.lus} élèves lus, tous déjà inscrits.`
          : `${bilan.inscrits} inscrits sur ${bilan.lus} lus` +
            (bilan.deja_presents ? ` (${bilan.deja_presents} déjà présents).` : "."),
      );
      setEleves(await api.lireEleves(classe.id));
      surChangement();
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  const supprimer = async (forcer: boolean) => {
    try {
      await api.supprimerClasse(classe.id, forcer);
      setImpact(null);
      surChangement();
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  return (
    <article className="fiche-classe">
      <header>
        <button type="button" onClick={basculer} className="titre-classe">
          {ouverte ? "▾" : "▸"} <strong>{classe.code}</strong> · {classe.niveau_libelle} ·{" "}
          {classe.annee_scolaire}
        </button>
        <span className="chiffres">
          {classe.eleves} élèves · {classe.devoirs} devoirs
          <button
            type="button"
            className="retirer toujours-visible"
            title="Supprimer cette classe"
            onClick={() => api.impactClasse(classe.id).then(setImpact)}
          >
            ×
          </button>
        </span>
      </header>

      {supprimable && impact && (
        <div className="confirmation">
          <p>
            Supprimer <strong>{impact.code}</strong> effacera{" "}
            <strong>{impact.devoirs.length} devoirs</strong> et{" "}
            <strong>{impact.notes_saisies} notes saisies</strong>
            {impact.qcm.length > 0 && (
              <>
                , ainsi que <strong>{impact.qcm.length} QCM</strong> et{" "}
                <strong>{impact.reponses_saisies} réponses saisies</strong>
              </>
            )}
            . Les {impact.eleves} élèves sont conservés.
          </p>
          {(impact.devoirs.length > 0 || impact.qcm.length > 0) && (
            <ul>
              {impact.devoirs.map((devoir) => (
                <li key={`ds-${devoir.id}`}>{devoir.titre}</li>
              ))}
              {impact.qcm.map((evaluation) => (
                <li key={`qcm-${evaluation.id}`}>{evaluation.titre} (QCM)</li>
              ))}
            </ul>
          )}
          <p>
            Une sauvegarde complète — élèves, devoirs et QCM — sera déposée dans
            la corbeille : la classe restera restaurable. Les questions de la
            banque, elles, ne sont pas touchées : l'export n'en porte que les
            identifiants, et c'est là qu'un QCM restauré les retrouvera.
          </p>
          <button
            type="button"
            className="danger"
            onClick={() =>
              supprimer(impact.devoirs.length > 0 || impact.qcm.length > 0)
            }
          >
            Confirmer la suppression
          </button>
          <button type="button" onClick={() => setImpact(null)}>
            Annuler
          </button>
        </div>
      )}

      {ouverte && (
        <div className="detail-classe">
          <div className="actions">
            <input
              ref={fichier}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              onChange={(e) => {
                const choisi = e.target.files?.[0];
                if (choisi) importer(choisi);
                e.target.value = "";
              }}
              hidden
            />
            <button type="button" onClick={() => fichier.current?.click()}>
              Importer une liste
            </button>
            <span className="aide">
              Formats : « Prénom⇥Nom » ou « NOM,Prénom ». Réimporter ne crée pas de doublon.
            </span>
          </div>

          {message && <p className="message">{message}</p>}

          {eleves && eleves.length > 0 && (
            <ol className="liste-eleves">
              {eleves.map((eleve) => (
                <li key={eleve.id}>
                  {eleve.nom} {eleve.prenom}
                  <button
                    type="button"
                    className="retirer"
                    title="Retirer de la classe"
                    onClick={async () => {
                      await api.desinscrire(classe.id, eleve.id);
                      setEleves(await api.lireEleves(classe.id));
                      surChangement();
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ol>
          )}
          {eleves && eleves.length === 0 && <p>Aucun élève inscrit.</p>}
        </div>
      )}
    </article>
  );
}

function NouvelleClasse({ surCreation }: { surCreation: () => void }) {
  const [code, setCode] = useState("");
  const [niveau, setNiveau] = useState(NIVEAUX[0].code);
  const [annee, setAnnee] = useState(anneeScolaireCourante());
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    try {
      await api.creerClasse({ code, niveau, annee_scolaire: annee });
      setCode("");
      setErreur(null);
      surCreation();
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  return (
    <form className="nouvelle-classe" onSubmit={creer}>
      <h3>Nouvelle classe</h3>
      <div>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Nom, ex. 201"
          required
        />
        <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
          {NIVEAUX.map((n) => (
            <option key={n.code} value={n.code}>
              {n.libelle}
            </option>
          ))}
        </select>
        <input
          value={annee}
          onChange={(e) => setAnnee(e.target.value)}
          pattern="\d{4}-\d{4}"
          title="Format attendu : 2025-2026"
          required
        />
        <button type="submit">Créer</button>
      </div>
      {erreur && <p className="erreur">{erreur}</p>}
    </form>
  );
}
